"use client";

import { useEffect, useRef, useState } from "react";

/* ————— fol. 1r — Incipit (hero) —————
   The manuscript in darkness; a lamp of warm light moves across it.
   Two stacked copies of the same image: the base sits dim; the top
   copy is brightened and masked by a radial gradient whose center is
   a CSS var — driven by a slow autonomous drift (time-based rAF) and,
   on fine pointers, a lerped mouse-follow. No scroll scrubbing here.

   LCP rule: the <h1> is fully visible at first paint — only the
   supporting elements choreograph in.
*/

const APP_STORE_URL = "https://apps.apple.com/us/app/sola-bible-app/id6739747733";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.sola.bible";

/* Hero backdrop candidate under review (Jay, 2026-07-10):
   "photo" | "inked" | "greekfield" — see backdrop JSX below. */
const HERO_VARIANT: "photo" | "inked" | "greekfield" = "greekfield";

export default function Incipit() {
  const sectionRef = useRef<HTMLElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const section = sectionRef.current;
    if (!section) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      section.style.setProperty("--lx", "62%");
      section.style.setProperty("--ly", "38%");
      return;
    }

    let raf = 0;
    // lamp position (%), target and current, lerped for weight
    let cx = 70;
    let cy = 30;
    let tx = cx;
    let ty = cy;
    let pointerActive = false;
    let pointerIdleTimer: ReturnType<typeof setTimeout> | null = null;

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const r = section.getBoundingClientRect();
      if (e.clientY < r.top || e.clientY > r.bottom) return;
      pointerActive = true;
      tx = ((e.clientX - r.left) / r.width) * 100;
      ty = ((e.clientY - r.top) / r.height) * 100;
      if (pointerIdleTimer) clearTimeout(pointerIdleTimer);
      pointerIdleTimer = setTimeout(() => {
        pointerActive = false;
      }, 2600);
    };

    const tick = (t: number) => {
      if (!pointerActive) {
        // slow lissajous drift across the page — the reading lamp wanders
        const s = t / 1000;
        tx = 50 + 34 * Math.sin(s * 0.11);
        ty = 40 + 22 * Math.sin(s * 0.17 + 1.3);
      }
      cx += (tx - cx) * 0.035;
      cy += (ty - cy) * 0.035;
      section.style.setProperty("--lx", `${cx.toFixed(2)}%`);
      section.style.setProperty("--ly", `${cy.toFixed(2)}%`);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onPointerMove);
      if (pointerIdleTimer) clearTimeout(pointerIdleTimer);
    };
  }, []);

  // Tailwind can't see runtime-built class strings, so the delay is an
  // inline style; the transition itself is a static class.
  const chorCls = `transition-[opacity,transform] duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
    mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
  }`;

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100svh] overflow-hidden flex flex-col"
      style={{ ["--lx" as string]: "70%", ["--ly" as string]: "30%" }}
    >
      {/* Backdrop — three candidates, Jay review 2026-07-10.
          'photo'     = original manuscript photograph
          'inked'     = duotone "analyzed manuscript" treatment + survey grid
          'greekfield'= typographic: typeset koine Greek revealed by the lamp */}
      {HERO_VARIANT === "greekfield" ? (
        <div className="absolute inset-0" aria-hidden="true">
          {/* Faint typeset Greek field (real Phil 4:6 text) — desktop/tablet
              only; on mobile the flowing field sat behind every line of copy
              and read as clutter (Jay 2026-07-11). Mobile gets the epigraph
              below instead. */}
          <div className="absolute inset-0 hidden md:flex items-center justify-end overflow-hidden">
            <p
              className="font-scripture select-none pr-[4vw] max-w-[62vw] text-right"
              style={{
                fontSize: "clamp(2.6rem, 5.2vw, 5.2rem)",
                lineHeight: 1.32,
                color: "rgba(242,234,216,0.055)",
              }}
            >
              μηδὲν{" "}
              <span className="relative text-[rgba(232,194,106,0.38)]">
                μεριμνᾶτε
                <span className="absolute left-0 right-0 -bottom-1 h-px bg-[rgba(212,163,62,0.5)]" />
                {/* The gloss — the hero performs the product's core move:
                    the one glowing Greek word gets its plain English right
                    there. Without this, the Greek reads as a barrier;
                    with it, it's the before→after (Jay's note). Lives in
                    the always-visible base layer so it never vanishes
                    when the lamp wanders. */}
                {/* Gloss sits ABOVE the word, arrow pointing down at it —
                    below-the-word placement collided with the headline's
                    "actually" on wide viewports (Jay 2026-07-11). */}
                <span className="absolute right-0 bottom-full mb-3 hidden md:flex flex-col items-end whitespace-nowrap not-italic pointer-events-none">
                  <span className="block font-mono text-[0.65rem] tracking-[0.14em] uppercase text-[rgba(212,163,62,0.9)]">
                    merimnaō: &ldquo;do not be anxious&rdquo;
                  </span>
                  <span className="mt-1 block font-sans text-sm tracking-normal text-[rgba(242,234,216,0.78)]">
                    care that divides the mind
                  </span>
                  <span className="mt-2 mr-10 flex flex-col items-center">
                    <span className="block h-4 w-px bg-[rgba(212,163,62,0.55)]" />
                    <svg width="9" height="5" viewBox="0 0 9 5" aria-hidden="true" className="mt-[1px]">
                      <path d="M0.5 0.5 L4.5 4 L8.5 0.5" fill="none" stroke="rgba(212,163,62,0.7)" strokeWidth="1" />
                    </svg>
                  </span>
                </span>
              </span>
              , ἀλλ&rsquo; ἐν παντὶ τῇ προσευχῇ καὶ τῇ δεήσει μετὰ εὐχαριστίας τὰ αἰτήματα ὑμῶν γνωριζέσθω πρὸς τὸν θεόν
            </p>
          </div>
          {/* Lamp-lit copy of the same field */}
          <div
            className="absolute inset-0 hidden md:flex items-center justify-end overflow-hidden"
            style={{
              WebkitMaskImage:
                "radial-gradient(34rem 26rem at var(--lx) var(--ly), rgba(0,0,0,0.95), rgba(0,0,0,0.35) 48%, transparent 74%)",
              maskImage:
                "radial-gradient(34rem 26rem at var(--lx) var(--ly), rgba(0,0,0,0.95), rgba(0,0,0,0.35) 48%, transparent 74%)",
            }}
          >
            <p
              className="font-scripture select-none pr-[4vw] max-w-[62vw] text-right"
              style={{
                fontSize: "clamp(2.6rem, 5.2vw, 5.2rem)",
                lineHeight: 1.32,
                color: "rgba(242,234,216,0.30)",
              }}
            >
              μηδὲν <span className="relative text-[rgba(232,194,106,0.85)]">μεριμνᾶτε<span className="absolute left-0 right-0 -bottom-1 h-px bg-[rgba(212,163,62,0.9)]" /></span>, ἀλλ&rsquo; ἐν παντὶ τῇ προσευχῇ καὶ τῇ δεήσει μετὰ εὐχαριστίας τὰ αἰτήματα ὑμῶν γνωριζέσθω πρὸς τὸν θεόν
            </p>
          </div>
          {/* Apparatus mark — the intelligence layer (the word gloss
              itself lives attached to μεριμνᾶτε in the field above) */}
          <div className="absolute right-6 top-24 hidden lg:block">
            <p className="folio [writing-mode:vertical-rl] tracking-[0.2em]">ΠΡΟΣ ΦΙΛΙΠΠΗΣΙΟΥΣ · 4:6</p>
          </div>
          {/* bottom handoff into the ink */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(13,11,8,0.2) 0%, rgba(13,11,8,0) 30%, rgba(13,11,8,0) 60%, rgba(13,11,8,0.9) 88%, #0d0b08 100%)",
            }}
          />
        </div>
      ) : (
        <div className="absolute inset-0" aria-hidden="true">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={HERO_VARIANT === "inked" ? "/atmosphere/manuscript-inked-2400.webp" : "/atmosphere/manuscript-2400.webp"}
            alt=""
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "brightness(0.72)" }}
          />
          {/* Lit copy revealed by the lamp mask */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={HERO_VARIANT === "inked" ? "/atmosphere/manuscript-inked-2400.webp" : "/atmosphere/manuscript-2400.webp"}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              filter: HERO_VARIANT === "inked" ? "brightness(1.6) saturate(0.9)" : "brightness(1.75) saturate(1.18)",
              WebkitMaskImage:
                "radial-gradient(38rem 30rem at var(--lx) var(--ly), rgba(0,0,0,0.95), rgba(0,0,0,0.4) 45%, transparent 72%)",
              maskImage:
                "radial-gradient(38rem 30rem at var(--lx) var(--ly), rgba(0,0,0,0.95), rgba(0,0,0,0.4) 45%, transparent 72%)",
            }}
          />
          {HERO_VARIANT === "inked" && (
            <>
              {/* survey grid — the analysis layer */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, rgba(242,234,216,0.05) 0 1px, transparent 1px 96px)," +
                    "repeating-linear-gradient(to bottom, rgba(242,234,216,0.05) 0 1px, transparent 1px 96px)",
                }}
              />
              {/* apparatus annotations */}
              <div className="absolute right-8 top-28 hidden lg:block text-right">
                <p className="folio mb-1">cod. sinaiticus · fol. 288</p>
                <p className="folio">koine greek · uncial hand</p>
              </div>
            </>
          )}
          {/* Legibility vignette + seamless handoff into the ink below */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, rgba(13,11,8,0.82) 0%, rgba(13,11,8,0.45) 38%, rgba(13,11,8,0.15) 65%, rgba(13,11,8,0.35) 100%)," +
                "linear-gradient(to bottom, rgba(13,11,8,0.5) 0%, rgba(13,11,8,0) 30%, rgba(13,11,8,0) 55%, rgba(13,11,8,0.9) 88%, #0d0b08 100%)",
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="container relative z-10 flex-1 flex flex-col justify-center pt-28 pb-24">
        <div className="max-w-4xl">
          {/* Mobile epigraph — in flow so it can never collide with the
              rubric (the absolute version did). The gold word + its
              one-line gloss: captivation without background clutter. */}
          <div aria-hidden="true" className={`md:hidden text-right mb-10 ${chorCls}`} style={{ transitionDelay: "100ms" }}>
            <p className="font-scripture" style={{ fontSize: "2.1rem", lineHeight: 1.2, color: "rgba(242,234,216,0.16)" }}>
              μηδὲν{" "}
              <span className="relative text-[rgba(232,194,106,0.75)]">
                μεριμνᾶτε
                <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-[rgba(212,163,62,0.55)]" />
              </span>
              ,
            </p>
            <p className="mt-2.5 font-mono text-[0.6rem] tracking-[0.14em] uppercase text-[rgba(212,163,62,0.75)]">
              merimnaō: &ldquo;do not be anxious&rdquo;
            </p>
          </div>
          <div className="overflow-hidden">
            <p className={`rubric mb-5 ${chorCls}`} style={{ transitionDelay: "150ms" }}>
              Context · Original words · Study plans
            </p>
          </div>
          {/* Gold hairline draws in */}
          <div
            aria-hidden="true"
            className="h-px mb-8 origin-left bg-[rgba(212,163,62,0.55)] max-w-md transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] [transition-delay:350ms]"
            style={{ transform: mounted ? "scaleX(1)" : "scaleX(0)" }}
          />
          {/* LCP element — never opacity-gated */}
          <h1 className="text-hero font-display mb-8 text-balance">
            Understand what
            <br />
            the Bible is <em className="text-[#e8c26a] not-italic font-display italic">actually</em> saying.
          </h1>
          <p className={`text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl mb-10 ${chorCls}`} style={{ transitionDelay: "500ms" }}>
            Every verse comes with its world: the original words, the history,
            the why. Sola is the study companion that keeps Scripture at the center.
          </p>
          <div className={`flex flex-col sm:flex-row gap-4 ${chorCls}`} style={{ transitionDelay: "650ms" }}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
              Download for iOS
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
              Download for Android
            </a>
          </div>
          <p className={`text-sm text-muted-foreground mt-6 ${chorCls}`} style={{ transitionDelay: "800ms" }}>
            Free to download · iOS &amp; Android
          </p>
        </div>
      </div>

    </section>
  );
}
